// Predicate AST — the editor's in-memory representation of an
// `escalation_policies.match_custom_fields` jsonb tree.
//
// On the wire (what the backend reads / writes — see
// app/use_cases/agents/predicates/evaluate.rb) the field-first shape is:
//
//   { all: [
//     { "appuser.custom_fields.tier": { eq: "gold" } },
//     { any: [
//       { "appuser.custom_fields.lifetime_value": { gte: 10000 } },
//       { "convo.custom_fields.urgency": { eq: "high" } }
//     ]}
//   ]}
//
// Each leaf key is the dotted field path. The dotted scope-key is `appuser`
// or `convo` — NOT `agent_convo`. The model `parent_type` enum still uses
// `agent_convo`, which is what the picker tracks on the AST side; the
// serializer maps it to `convo` only when emitting JSON.
//
// Synthetic ops live UI-only. Right now `between` is the only one — it
// renders as one row with a pair-of-numbers (or pair-of-dates) control,
// but serializes as `{ all: [{ "<field path>": { gte: ... } }, { "<field path>": { lte: ... } }] }`. `fromJson`
// re-collapses that exact shape back to a single `between` row so editors
// see what they entered.

import type { CustomFieldDefinition, CustomFieldType } from "@/types/custom_field"

// Editor-side scope tracks the model `parent_type` enum for label clarity
// ("Conversation" not "Convo" in the picker). DottedScope is what
// serializes to JSON.
export type Scope = "appuser" | "agent_convo"
export type DottedScope = "appuser" | "convo"

export const dottedScopeFor = (s: Scope): DottedScope =>
  s === "appuser" ? "appuser" : "convo"

export const scopeFromDotted = (d: DottedScope): Scope =>
  d === "appuser" ? "appuser" : "agent_convo"

// Routing-builder leaf ops. The backend evaluator also understands
// skill/action-requirement-only ops (`has_attachment`, `mime_in`) that are
// intentionally not exposed here.
export type LeafOp =
  | "present"
  | "blank"
  | "eq"
  | "neq"
  | "contains"
  | "gte"
  | "lte"
  | "gt"
  | "lt"
  | "matches"

// `between` is a UI-only synonym; serializer expands it to all([gte,lte]).
export type DisplayOp = LeafOp | "between"

export type CompositeOp = "all" | "any"

// `system_state` is the fictional field-type for reserved system keys
// (currently just `source_state`). It serializes with no `custom_fields.`
// path segment — `appuser.<key>` rather than `appuser.custom_fields.<key>`.
export type FieldKind = CustomFieldType | "system_state"

export type LeafWarning =
  | "field_archived"
  | "field_missing"
  | "value_no_longer_in_choices"

export type LeafNode = {
  kind: "leaf"
  id: string
  scope: Scope
  fieldKey: string
  fieldType: FieldKind
  op: DisplayOp
  // string | number | boolean | string[] | [number,number] | [string,string]
  value: unknown
  warning?: LeafWarning
}

export type GroupNode = {
  kind: "group"
  id: string
  op: CompositeOp
  // When set, serializes as `{ not: { all|any: [...] } }`. The UI doesn't
  // currently wire a `not` toggle — kept on the type for forward-compat
  // with Forge proposals that may emit `not` blocks.
  negated?: boolean
  children: PredicateNode[]
}

export type PredicateNode = LeafNode | GroupNode

// Path into the AST tree — array of child indexes from the root group.
// `[]` is the root. `[0, 2]` is "first child of root, third child of that
// group". Used for immutable updates via `applyAt` / `removeAt`.
export type NodePath = number[]

export type CustomFieldCatalog = {
  appuser:     CustomFieldDefinition[]
  agent_convo: CustomFieldDefinition[]
}

// Flattened leaf as <PredicateSummary> renders it. Includes the resolved
// label so summary text doesn't need the catalog at render time. Used
// by both the policy-list-row summary AND Stream C's Forge diff card.
export type FlatLeaf = {
  scope: Scope
  fieldKey: string
  fieldLabel: string  // resolved via catalog; falls back to key if missing
  fieldType: FieldKind
  op: DisplayOp
  value: unknown
  warning?: LeafWarning
}

// -- ID helpers -----------------------------------------------------------

// Lightweight unique-id generator. ulid is overkill for in-memory React
// keys; a counter + random suffix is plenty and avoids a new dep.
let idSeq = 0
function nid(prefix: string): string {
  idSeq += 1
  return `${prefix}_${idSeq}_${Math.random().toString(36).slice(2, 7)}`
}

// -- Builders -------------------------------------------------------------

export function defaultGroup(op: CompositeOp): GroupNode {
  return { kind: "group", id: nid("g"), op, children: [] }
}

// Pick a sensible default op + value for a freshly-added leaf based on
// the field's type. Operators almost always want "is", so eq wins. The
// value is left as the empty string / 0 / false / [] — the operator
// fills it in immediately.
export function defaultLeaf(scope: Scope, fieldDef: CustomFieldLike): LeafNode {
  const ft: FieldKind = fieldDef.fieldType
  let op: DisplayOp = "eq"
  let value: unknown = ""
  switch (ft) {
    case "number":       op = "eq"; value = 0;       break
    case "boolean":      op = "eq"; value = true;    break
    case "date":         op = "eq"; value = "";      break
    case "select":
    case "system_state": op = "eq"; value = firstChoice(fieldDef) ?? ""; break
    case "multi_select": op = "contains"; value = []; break
    default:             op = "eq"; value = "";      break
  }
  return {
    kind: "leaf",
    id: nid("l"),
    scope,
    fieldKey: fieldDef.key,
    fieldType: ft,
    op,
    value,
  }
}

// -- Path-based immutable mutation ---------------------------------------

export function applyAt(
  root: PredicateNode,
  path: NodePath,
  mutator: (node: PredicateNode) => PredicateNode,
): PredicateNode {
  if (path.length === 0) return mutator(root)
  if (root.kind !== "group") return root
  const [head, ...rest] = path
  const nextChildren = root.children.map((child, i) =>
    i === head ? applyAt(child, rest, mutator) : child,
  )
  return { ...root, children: nextChildren }
}

export function removeAt(
  root: PredicateNode,
  path: NodePath,
): PredicateNode | null {
  if (path.length === 0) return null
  if (root.kind !== "group") return root
  if (path.length === 1) {
    const idx = path[0]
    const nextChildren = root.children.filter((_, i) => i !== idx)
    return { ...root, children: nextChildren }
  }
  const [head, ...rest] = path
  const nextChildren = root.children.map((child, i) => {
    if (i !== head) return child
    const next = removeAt(child, rest)
    return next ?? null
  }).filter((c): c is PredicateNode => c !== null)
  return { ...root, children: nextChildren }
}

// Convenience: insert a child at the end of a group at `path`.
export function insertAt(
  root: PredicateNode,
  path: NodePath,
  child: PredicateNode,
): PredicateNode {
  return applyAt(root, path, (n) => {
    if (n.kind !== "group") return n
    return { ...n, children: [...n.children, child] }
  })
}

// -- Serialize ------------------------------------------------------------

// Turn an AST into the wire-shape JSON the backend evaluator accepts.
// Empty groups serialize to {} (matches everything). A `null` AST or a
// single-child group are flattened to keep the JSON readable.
export function toJson(ast: PredicateNode | null): Record<string, unknown> {
  if (!ast) return {}
  return serializeNode(ast)
}

function serializeNode(node: PredicateNode): Record<string, unknown> {
  if (node.kind === "leaf") return serializeLeaf(node)
  // group
  const childJson = node.children.map(serializeNode).filter(notEmptyObj)
  if (childJson.length === 0) return {}
  // Preserve single-child groups as-is. We used to collapse them for
  // readability, but that silently flattened intentional sub-groups
  // created by "+ Add group" — making the button indistinguishable
  // from "+ Add condition" once the AST round-tripped through JSON.
  const inner: Record<string, unknown> = { [node.op]: childJson }
  return node.negated ? { not: inner } : inner
}

function serializeLeaf(leaf: LeafNode): Record<string, unknown> {
  const field = dottedFieldFor(leaf)
  if (leaf.op === "between") {
    const v = leaf.value as [number | string, number | string] | undefined
    const lo = Array.isArray(v) ? v[0] : undefined
    const hi = Array.isArray(v) ? v[1] : undefined
    return {
      all: [
        { [field]: { gte: lo } },
        { [field]: { lte: hi } },
      ],
    }
  }
  if (leaf.op === "contains") {
    return { [field]: { contains: Array.isArray(leaf.value) ? leaf.value : leaf.value } }
  }
  if (leaf.op === "present" || leaf.op === "blank") {
    return { [field]: { [leaf.op]: true } }
  }
  if (leaf.op === "matches") {
    return { [field]: { matches: String(leaf.value ?? "") } }
  }
  return { [field]: { [leaf.op]: leaf.value } }
}

export function dottedFieldFor(leaf: LeafNode): string {
  const ds = dottedScopeFor(leaf.scope)
  if (leaf.fieldType === "system_state") return `${ds}.${leaf.fieldKey}`
  return `${ds}.custom_fields.${leaf.fieldKey}`
}

function notEmptyObj(o: Record<string, unknown>): boolean {
  return Object.keys(o).length > 0
}

// -- Deserialize ----------------------------------------------------------

export type ParseResult =
  | { ok: true; ast: PredicateNode }
  | { ok: false; reason: string }

export function fromJson(json: unknown): ParseResult {
  if (json === null || json === undefined) {
    return { ok: true, ast: defaultGroup("all") }
  }
  if (typeof json !== "object" || Array.isArray(json)) {
    return { ok: false, reason: "Top-level predicate must be an object" }
  }
  const obj = json as Record<string, unknown>
  if (Object.keys(obj).length === 0) {
    return { ok: true, ast: defaultGroup("all") }
  }
  try {
    const node = parseNode(obj)
    // If the top-level parsed to a leaf, wrap it in an implicit `all`
    // group so the editor always renders a root group with N children.
    if (node.kind === "leaf") {
      return {
        ok: true,
        ast: { ...defaultGroup("all"), children: [node] },
      }
    }
    return { ok: true, ast: node }
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : "Malformed predicate" }
  }
}

function parseNode(obj: Record<string, unknown>): PredicateNode {
  const keys = Object.keys(obj)
  if (keys.length !== 1) {
    throw new Error(`Node must have exactly one key, got ${keys.length}`)
  }
  const key = keys[0]
  const value = obj[key]

  if (key === "all" || key === "any") {
    if (!Array.isArray(value)) throw new Error(`'${key}' must be an array`)
    const childrenRaw = value.map((c) => parseChildHash(c))
    // try to recognize the synthetic `between` shape
    if (key === "all") {
      const between = tryParseBetween(childrenRaw)
      if (between) return between
    }
    return { kind: "group", id: nid("g"), op: key, children: childrenRaw }
  }
  if (key === "not") {
    if (typeof value !== "object" || value === null) throw new Error("'not' must wrap an object")
    const inner = parseNode(value as Record<string, unknown>)
    if (inner.kind === "group") {
      return { ...inner, negated: !inner.negated }
    }
    // `not` of a leaf — wrap it in an `all`-group with negated flag set
    return {
      kind: "group",
      id: nid("g"),
      op: "all",
      negated: true,
      children: [inner],
    }
  }
  // field-first leaf
  return parseLeaf(key, value)
}

function parseChildHash(c: unknown): PredicateNode {
  if (typeof c !== "object" || c === null || Array.isArray(c)) {
    throw new Error("Group child must be an object")
  }
  return parseNode(c as Record<string, unknown>)
}

function parseLeaf(field: string, expression: unknown): LeafNode {
  if (typeof expression !== "object" || expression === null || Array.isArray(expression)) {
    throw new Error(`Field '${field}' must have an operator object`)
  }
  const a = expression as Record<string, unknown>
  const keys = Object.keys(a)
  if (keys.length !== 1) throw new Error(`Field '${field}' must have exactly one operator`)
  const op = keys[0]
  const raw = a[op]
  const { scope, fieldKey, fieldType } = parseDotted(field)

  let value: unknown
  if (op === "contains") {
    value = Array.isArray(raw) ? raw : []
  } else if (op === "present" || op === "blank") {
    value = ""
  } else if (op === "matches") {
    value = typeof raw === "string" ? raw : ""
  } else {
    value = raw
  }

  if (!isLeafOp(op)) throw new Error(`Unknown leaf op: ${op}`)

  return {
    kind: "leaf",
    id: nid("l"),
    scope,
    fieldKey,
    fieldType,
    op,
    value,
  }
}

function isLeafOp(s: string): s is LeafOp {
  return [
    "present", "blank", "eq", "neq", "contains",
    "gte", "lte", "gt", "lt", "matches",
  ].includes(s)
}

// Pull (scope, fieldKey, fieldType) from a dotted field path. We do not
// have the catalog here, so fieldType defaults to "text" — the editor
// resolves it after parse via a catalog lookup (see hydrateAst).
function parseDotted(field: string): {
  scope: Scope
  fieldKey: string
  fieldType: FieldKind
} {
  const parts = field.split(".")
  const head = parts[0]
  const scope: Scope = head === "appuser" ? "appuser" : "agent_convo"
  if (parts.length === 2) {
    // system field — `appuser.source_state`
    const key = parts[1]
    return { scope, fieldKey: key, fieldType: "system_state" }
  }
  if (parts[1] === "custom_fields" && parts.length >= 3) {
    return { scope, fieldKey: parts.slice(2).join("."), fieldType: "text" }
  }
  // Fallback — treat as a system field-path with the trailing segment.
  return {
    scope,
    fieldKey: parts.slice(1).join("."),
    fieldType: "text",
  }
}

// `between` recognizer — collapse `{ all: [{ F:{ gte: lo }}, { F:{ lte: hi }}] }`
// (in either order) back to a single `between` leaf.
function tryParseBetween(children: PredicateNode[]): LeafNode | null {
  if (children.length !== 2) return null
  const [a, b] = children
  if (a.kind !== "leaf" || b.kind !== "leaf") return null
  if (a.fieldKey !== b.fieldKey || a.scope !== b.scope) return null
  const ops = new Set([a.op, b.op])
  if (!(ops.has("gte") && ops.has("lte"))) return null
  const lo = a.op === "gte" ? a.value : b.value
  const hi = a.op === "lte" ? a.value : b.value
  return {
    kind: "leaf",
    id: nid("l"),
    scope: a.scope,
    fieldKey: a.fieldKey,
    fieldType: a.fieldType,
    op: "between",
    value: [lo, hi],
  }
}

// -- Hydration ------------------------------------------------------------

// After fromJson() we know the scope + key but `fieldType` defaults to
// "text" because the JSON doesn't carry it. Walk the AST post-parse and
// resolve real types from the catalog. Also tags warnings for fields
// whose definition is archived, missing, or whose stored `value` isn't
// in the current `options.choices` list.
export function hydrateAst(
  ast: PredicateNode,
  catalog: CustomFieldCatalog,
): PredicateNode {
  if (ast.kind === "leaf") {
    return hydrateLeaf(ast, catalog)
  }
  return {
    ...ast,
    children: ast.children.map((c) => hydrateAst(c, catalog)),
  }
}

function hydrateLeaf(leaf: LeafNode, catalog: CustomFieldCatalog): LeafNode {
  if (leaf.fieldType === "system_state") return leaf
  const def = lookupDef(catalog, leaf.scope, leaf.fieldKey)
  if (!def) return { ...leaf, warning: "field_missing" }

  let warning: LeafWarning | undefined
  if (def.status === "archived") warning = "field_archived"
  if (!warning && (def.fieldType === "select" || def.fieldType === "multi_select")) {
    const choices = pickChoices(def)
    if (choices.length > 0) {
      const allowed = new Set(choices.map((c) => c.value))
      const v = leaf.value
      if (Array.isArray(v)) {
        if (v.some((x) => !allowed.has(String(x)))) {
          warning = "value_no_longer_in_choices"
        }
      } else if (typeof v === "string" && v && !allowed.has(v)) {
        warning = "value_no_longer_in_choices"
      }
    }
  }

  return {
    ...leaf,
    fieldType: def.fieldType,
    warning,
  }
}

function lookupDef(
  catalog: CustomFieldCatalog,
  scope: Scope,
  key: string,
): CustomFieldDefinition | null {
  const list = scope === "appuser" ? catalog.appuser : catalog.agent_convo
  return list.find((d) => d.key === key) ?? null
}

// -- Flatten --------------------------------------------------------------

export function flatten(
  ast: PredicateNode | null,
  catalog: CustomFieldCatalog,
): FlatLeaf[] {
  if (!ast) return []
  const out: FlatLeaf[] = []
  walkLeaves(ast, (leaf) => {
    out.push({
      scope: leaf.scope,
      fieldKey: leaf.fieldKey,
      fieldLabel: resolveLabel(catalog, leaf),
      fieldType: leaf.fieldType,
      op: leaf.op,
      value: leaf.value,
      warning: leaf.warning,
    })
  })
  return out
}

function walkLeaves(node: PredicateNode, cb: (leaf: LeafNode) => void): void {
  if (node.kind === "leaf") { cb(node); return }
  for (const c of node.children) walkLeaves(c, cb)
}

function resolveLabel(catalog: CustomFieldCatalog, leaf: LeafNode): string {
  if (leaf.fieldType === "system_state" && leaf.fieldKey === "source_state") {
    return "Source state"
  }
  const def = lookupDef(catalog, leaf.scope, leaf.fieldKey)
  return def?.label ?? leaf.fieldKey
}

// -- Field-pickerland helpers --------------------------------------------

// Trimmed shape that defaultLeaf needs from a CustomFieldDefinition (or a
// fabricated entry for a system field). Lets the system-fields rail pass
// in a synthetic "source_state" def without minting a fake catalog row.
export type CustomFieldLike = {
  key: string
  fieldType: FieldKind
  options?: Record<string, unknown>
}

export type Choice = { value: string; label: string; color: string | null }

export function pickChoices(def: CustomFieldLike): Choice[] {
  const opts = (def.options ?? {}) as Record<string, unknown>
  const raw = opts.choices
  if (!Array.isArray(raw)) return []
  const out: Choice[] = []
  for (const c of raw) {
    if (!c || typeof c !== "object") continue
    const r = c as Record<string, unknown>
    const value = typeof r.value === "string" ? r.value : null
    if (!value) continue
    const label = typeof r.label === "string" ? r.label : value
    const color = typeof r.color === "string" ? r.color : null
    out.push({ value, label, color })
  }
  return out
}

function firstChoice(def: CustomFieldLike): string | null {
  const list = pickChoices(def)
  return list.length > 0 ? list[0].value : null
}

// Synthetic field-definition for the reserved `source_state` system key.
// Used by the FieldPicker's pinned "System fields" group; the picker
// reads the choice list off the catalog entry if present (operators
// curate the list as a regular live field), and falls back to a hardcoded
// triple of common values if not seeded yet.
export const SYSTEM_FIELDS = [
  {
    key:       "source_state",
    label:     "Source state",
    fieldType: "system_state" as const,
  },
]
